<template>
  <v-container class="pt-0 pb-5">
    <v-row>
      <v-col>
        <div class="pa-2 rounded elevation-1 bg-white">
          <SportipsButton backButton chevronLeft>Retour</SportipsButton>
          <div class="text-h6 font-weight-bold mt-3 mb-2">Gestion des équipes</div>



          <v-progress-linear v-if="loading" indeterminate color="primary-dark" class="mb-4" />

          <v-alert v-if="loadError" type="error" variant="tonal" class="mb-4" density="compact">
            Impossible de charger la liste des équipes.
          </v-alert>

          <div v-else-if="!loading && equipes.length === 0" class="text-body-1 text-medium-emphasis">
            Aucune équipe sur ce module pour l’instant.
          </div>



          <v-expansion-panels v-else multiple v-model="expandedPanels" class="mb-2">
            <v-expansion-panel v-for="(equipe, idx) in equipes" :key="equipePanelKey(equipe, idx)" :value="idx">
              <v-expansion-panel-title class="text-wrap">
                <div class="d-flex flex-column align-start">
                  <div class="d-flex flex-row align-center ga-2">
                    <v-icon start size="small"
                      :color="equipe.equipe_complete ? 'success' : 'error'">mdi-account-group</v-icon>
                    <v-icon start size="small"
                      :color="equipe.possede_chef_equipe ? 'success' : 'error'">mdi-crown</v-icon>
                    <span class="font-weight-bold">{{ equipe.nom_equipe || '—' }}</span>
                  </div>

                  <span class="text-caption text-medium-emphasis">{{ equipe.nom_parcours }} <span
                      class="text-primary-dark font-weight-bold">|</span> {{
                        equipe.inscriptions.length }}/{{ equipe.expected }} participants <span
                      class="text-primary-dark font-weight-bold">|</span> {{ equipe.commandes.length }} commandes</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="d-flex flex-wrap align-center ga-2 mb-3">
                  <SportipsButton icon="mdi-rename-box" @click="openRenameGroupe(equipe)">
                    Renommer toute l’équipe
                  </SportipsButton>

                </div>



                <v-table density="compact">
                  <thead>
                    <tr>
                      <th class="text-left">Participant</th>
                      <th class="text-left">Sexe</th>
                      <th class="text-right">Dossard</th>
                      <th class="text-right">Commande</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ins in equipe.inscriptions" :key="ins.id">
                      <td>
                        <router-link :to="editInscriptionTo(ins.id)" class="text-primary text-decoration-none">
                          <v-icon v-if="ins.is_captain_equipe" color="primary-dark" icon="mdi-crown"
                            class="mr-1"></v-icon>{{
                              ins.prenom }} {{ ins.nom }}
                        </router-link>
                      </td>
                      <td>{{ ins.sexe }}</td>
                      <td class="text-right">{{ ins.dossard }}</td>
                      <td class="text-right">{{ ins.id_commande }}</td>
                      <td class="text-right">
                        <div class="d-flex flex-column align-end ga-1">
                          <v-btn v-if="!ins.is_captain_equipe" size="x-small" variant="text" color="primary-dark"
                            :loading="chefActionId === ins.id" @click="definirChefEquipe(equipe, ins)">
                            Définir comme chef d’équipe
                          </v-btn>
                          <v-btn size="x-small" variant="text" color="primary-dark"
                            @click="openMoveParticipant(equipe, ins)">
                            Changer d’équipe
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogRenameGroupe" max-width="520" scroll-strategy="none">
      <v-card v-if="equipeRenameTarget">
        <v-card-title>Renommer l’équipe</v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3">
            Parcours : <b>{{ equipeRenameTarget.nom_parcours }}</b><br />
            Nom actuel : <b>{{ equipeRenameTarget.nom_equipe }}</b>
          </div>
          <v-text-field v-model="nouveauNomGroupe" label="Nouveau nom d’équipe"
            hint="Majuscules, chiffres et caractères autorisés (cf. règles inscription)" persistent-hint
            :rules="rulesRenameGroupe()" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogRenameGroupe = false">Annuler</v-btn>
          <v-btn color="primary-dark" :loading="actionLoading" @click="submitRenameGroupe">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogMoveParticipant" max-width="520" scroll-strategy="none">
      <v-card v-if="moveContext">
        <v-card-title>Changer d’équipe (un participant)</v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3">
            <b>{{ moveContext.inscription.prenom }} {{ moveContext.inscription.nom }}</b><br />
            Équipe actuelle : <b>{{ moveContext.equipe.nom_equipe }}</b> — {{ moveContext.equipe.nom_parcours }}
          </div>
          <v-text-field v-model="nouveauNomParticipant" label="Nom de l’équipe de destination"
            hint="Saisir le nom exact d’une équipe existante ou un nouveau nom valide" persistent-hint
            :rules="rulesMoveParticipant()" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogMoveParticipant = false">Annuler</v-btn>
          <v-btn color="primary-dark" :loading="actionLoading" @click="submitMoveParticipant">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import AdminService from "@/services/admin.service";

export default {
  data() {
    return {
      equipes: [],
      loading: false,
      loadError: false,
      expandedPanels: [],
      dialogRenameGroupe: false,
      equipeRenameTarget: null,
      nouveauNomGroupe: "",
      dialogMoveParticipant: false,
      moveContext: null,
      nouveauNomParticipant: "",
      actionLoading: false,
      chefActionId: null,
    };
  },
  computed: {
    ...mapStores(useMainStore),
  },
  created() {
    this.fetchEquipes(true);
  },
  methods: {
    equipePanelKey(equipe, idx) {
      return `${equipe.id_course}-${equipe.nom_equipe}-${idx}`;
    },
    editInscriptionTo(id_inscription) {
      return {
        name: "EditInscriptionAdmin",
        params: {
          base_module: this.$route.params.base_module,
          id_inscription: String(id_inscription),
        },
      };
    },
    refundCommandeTo(id_commande) {
      return {
        name: "RefundCommande",
        params: {
          base_module: this.$route.params.base_module,
          id_commande: String(id_commande),
        },
      };
    },
    async fetchEquipes(with_auto_assign_chef_equipe = false) {
      this.loading = true;
      this.loadError = false;
      const id_module = this.mainStore.module.id;
      const with_auto_assign_chef_equipe_param = with_auto_assign_chef_equipe ? "1" : "0";
      const data = await AdminService.getEquipesModule(id_module, with_auto_assign_chef_equipe_param);
      this.loading = false;
      if (!data || !Array.isArray(data.equipes)) {
        this.loadError = !data;
        this.equipes = [];
        return;
      }
      this.equipes = data.equipes;
    },
    equipesMemeParcours(id_course) {
      return this.equipes.filter((e) => e.id_course === id_course).map((e) => ({ nom: e.nom_equipe }));
    },
    indexEquipePourRegle(equipe) {
      return this.equipes
        .filter((e) => e.id_course === equipe.id_course)
        .findIndex((e) => e.nom_equipe === equipe.nom_equipe);
    },
    rulesRenameGroupe() {
      return [
        (v) => {
          if (!this.equipeRenameTarget) return true;
          const list = this.equipesMemeParcours(this.equipeRenameTarget.id_course);
          const idx = this.indexEquipePourRegle(this.equipeRenameTarget);
          return this.$formRules.equipe((v ?? "").trim(), idx, list);
        },
      ];
    },
    rulesMoveParticipant() {
      return [
        (v) => {
          const val = (v ?? "").trim();
          return this.$formRules.equipe(val, 0, [{ nom: val }]);
        },
      ];
    },
    validateRenameGroupeValue() {
      if (!this.equipeRenameTarget) return "Erreur interne.";
      const list = this.equipesMemeParcours(this.equipeRenameTarget.id_course);
      const idx = this.indexEquipePourRegle(this.equipeRenameTarget);
      return this.$formRules.equipe(this.nouveauNomGroupe?.trim() || "", idx, list);
    },
    validateMoveParticipantValue() {
      const val = this.nouveauNomParticipant?.trim() || "";
      return this.$formRules.equipe(val, 0, [{ nom: val }]);
    },
    openRenameGroupe(equipe) {
      this.equipeRenameTarget = equipe;
      this.nouveauNomGroupe = equipe.nom_equipe || "";
      this.dialogRenameGroupe = true;
    },
    openMoveParticipant(equipe, inscription) {
      this.moveContext = { equipe, inscription };
      this.nouveauNomParticipant = "";
      this.dialogMoveParticipant = true;
    },
    isApiSuccess(res) {
      if (res === false || res == null) return false;
      if (typeof res === "object" && res.error) return false;
      return true;
    },
    idAncienChefEquipe(equipe) {
      const chef = equipe.inscriptions?.find((i) => i.is_captain_equipe);
      return chef?.id ?? null;
    },
    async definirChefEquipe(equipe, inscription) {
      if (inscription.is_captain_equipe) return;
      const id_ancien = this.idAncienChefEquipe(equipe);
      this.chefActionId = inscription.id;
      const payload = {
        id_module: this.mainStore.module.id,
        id_inscription: inscription.id,
      };
      if (id_ancien != null && id_ancien !== inscription.id) {
        payload.id_ancien_chef_inscription = id_ancien;
      }
      const res = await AdminService.setChefEquipe(payload);
      this.chefActionId = null;
      if (this.isApiSuccess(res)) {
        this.mainStore.notification.send("Chef d’équipe mis à jour.", "success");
        await this.fetchEquipes();
      } else {
        const msg = typeof res === "object" && res?.error ? res.error : "Impossible de définir le chef d’équipe.";
        this.mainStore.notification.send(msg, "error");
      }
    },
    async submitRenameGroupe() {
      const check = this.validateRenameGroupeValue();
      if (check !== true) {
        this.mainStore.notification.send(typeof check === "string" ? check : "Nom d’équipe invalide", "error");
        return;
      }
      const ids_inscriptions = this.equipeRenameTarget.inscriptions.map((ins) => ins.id).join(",");

      this.actionLoading = true;
      const res = await AdminService.renameEquipeGroupe({
        id_module: this.mainStore.module.id,
        old_nom_equipe: this.equipeRenameTarget.nom_equipe,
        nouveau_nom_equipe: this.nouveauNomGroupe.trim(),
        ids_inscriptions: ids_inscriptions,
      });
      this.actionLoading = false;
      if (this.isApiSuccess(res)) {
        this.mainStore.notification.send("Équipe renommée.", "success");
        this.dialogRenameGroupe = false;
        await this.fetchEquipes();
      } else {
        const msg = typeof res === "object" && res?.error ? res.error : "Échec du renommage.";
        this.mainStore.notification.send(msg, "error");
      }
    },
    async submitMoveParticipant() {
      const check = this.validateMoveParticipantValue();
      if (check !== true) {
        this.mainStore.notification.send(typeof check === "string" ? check : "Nom d’équipe invalide", "error");
        return;
      }
      this.actionLoading = true;
      const res = await AdminService.setEquipeNomInscription({
        id_module: this.mainStore.module.id,
        old_nom_equipe: this.moveContext.equipe.nom_equipe,
        id_inscription: this.moveContext.inscription.id,
        nouveau_nom_equipe: this.nouveauNomParticipant.trim(),
      });
      this.actionLoading = false;
      if (this.isApiSuccess(res)) {
        this.mainStore.notification.send("Participant déplacé vers la nouvelle équipe.", "success");
        this.dialogMoveParticipant = false;
        await this.fetchEquipes();
      } else {
        const msg = typeof res === "object" && res?.error ? res.error : "Échec de la mise à jour.";
        this.mainStore.notification.send(msg, "error");
      }
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
